import { NameIndex, type MentionableEntry } from '@realmery/realm-core';
import { Plugin, PluginKey, type EditorState, type Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { entrySchema } from './schema.ts';

/**
 * The mention picker (docs/ENTRY-EDITOR-SLICE-2-PLAN.md section 4.5): typing `[[` lists
 * entries whose names match what follows, and choosing one replaces `[[…` with a mention
 * link, written as the desktop writes them. A ProseMirror plugin of our own: it watches
 * the text before the cursor, keeps the matches and the highlighted one in its state, and
 * handles the keys. The list itself is drawn by React (RichTextEditor.tsx).
 */

export interface MentionCandidate extends MentionableEntry {
  /** The type as shown, such as "Place" or a custom type's own name. */
  readonly typeName: string;
}

export interface PickerMatch {
  readonly entry: MentionCandidate;
  /** The text the link gets: an alias when one was typed, otherwise the title. */
  readonly text: string;
}

export interface OpenPicker {
  /** Where `[[` starts, and the cursor: the range a choice replaces. */
  readonly from: number;
  readonly to: number;
  readonly query: string;
  readonly matches: readonly PickerMatch[];
  readonly selected: number;
}

interface PickerState {
  readonly open: OpenPicker | null;
  /** A `[[` closed with Escape, by position, so the list stays closed for it. */
  readonly dismissed: number | null;
}

type PickerMeta = { dismiss: true } | { select: number };

export const mentionPickerKey = new PluginKey<PickerState>('mention-picker');

const TRIGGER = '[[';
const CLOSE = ']]';
/** The longest query: past this, the list closes. */
export const QUERY_MAX = 40;
export const MATCHES_MAX = 8;
/** What `textBetween` puts for a hard break or other leaf, which ends a query. */
const LEAF = '￼';

const names = new WeakMap<MentionCandidate, string[]>();
function namesOf(entry: MentionCandidate): string[] {
  let found = names.get(entry);
  if (!found) {
    found = NameIndex.namesFor(entry);
    names.set(entry, found);
  }
  return found;
}

/**
 * Entries matching `query` by title, alias or short name: exact matches first, then
 * names that start with it, then names that contain it. Each entry shows once.
 */
export function pickerMatches(candidates: readonly MentionCandidate[], query: string, max = MATCHES_MAX): PickerMatch[] {
  const q = query.trim().toLowerCase();
  const ranked: { match: PickerMatch; rank: number }[] = [];
  for (const entry of candidates) {
    let best: { rank: number; name: string } | null = null;
    for (const name of namesOf(entry)) {
      const lower = name.toLowerCase();
      const rank = lower === q ? 0 : lower.startsWith(q) ? 1 : lower.includes(q) ? 2 : -1;
      if (rank >= 0 && (best === null || rank < best.rank)) best = { rank, name };
    }
    if (!best) continue;
    const matched = best.name.toLowerCase();
    const alias = entry.aliases.find((a) => a.trim().toLowerCase() === matched);
    ranked.push({ match: { entry, text: alias?.trim() ?? entry.title.trim() }, rank: best.rank });
  }
  ranked.sort((a, b) => a.rank - b.rank || a.match.entry.title.localeCompare(b.match.entry.title, undefined, { sensitivity: 'base' }));
  return ranked.slice(0, max).map((r) => r.match);
}

/** The `[[` before the cursor and what's typed after it, if the cursor is in one. */
function trigger(state: EditorState): { from: number; query: string } | null {
  const { selection } = state;
  if (!selection.empty) return null;
  const $from = selection.$from;
  if (!$from.parent.isTextblock) return null;
  const before = $from.parent.textBetween(0, $from.parentOffset, undefined, LEAF);
  const at = before.lastIndexOf(TRIGGER);
  if (at < 0) return null;
  const query = before.slice(at + TRIGGER.length);
  if (query.length > QUERY_MAX || query.includes(CLOSE) || query.includes(LEAF)) return null;
  return { from: $from.start() + at, query };
}

export function mentionPicker(candidates: () => readonly MentionCandidate[]): Plugin<PickerState> {
  return new Plugin<PickerState>({
    key: mentionPickerKey,
    state: {
      init: () => ({ open: null, dismissed: null }),
      apply(tr: Transaction, value: PickerState, _old: EditorState, state: EditorState): PickerState {
        const meta = tr.getMeta(mentionPickerKey) as PickerMeta | undefined;
        if (meta && 'dismiss' in meta) return { open: null, dismissed: value.open?.from ?? value.dismissed };
        if (meta && 'select' in meta) {
          return value.open ? { ...value, open: { ...value.open, selected: meta.select } } : value;
        }
        const dismissed = value.dismissed === null ? null : tr.mapping.map(value.dismissed);
        const found = trigger(state);
        if (!found) return { open: null, dismissed: null };
        if (found.from === dismissed) return { open: null, dismissed };
        const same = value.open && value.open.from === found.from && value.open.query === found.query;
        return {
          open: {
            from: found.from,
            to: state.selection.from,
            query: found.query,
            matches: same ? value.open!.matches : pickerMatches(candidates(), found.query),
            selected: same ? value.open!.selected : 0,
          },
          dismissed: null,
        };
      },
    },
    props: {
      handleKeyDown(view, event) {
        const open = mentionPickerKey.getState(view.state)?.open;
        if (!open || event.isComposing) return false;
        const count = open.matches.length;
        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowUp':
            if (count === 0) return false;
            selectMatch(view, (open.selected + (event.key === 'ArrowDown' ? 1 : count - 1)) % count);
            return true;
          case 'Enter':
          case 'Tab':
            if (count === 0) return false;
            chooseMatch(view, open.selected);
            return true;
          case 'Escape':
            dismissPicker(view);
            return true;
          default:
            return false;
        }
      },
    },
  });
}

export function openPicker(state: EditorState): OpenPicker | null {
  return mentionPickerKey.getState(state)?.open ?? null;
}

export function selectMatch(view: EditorView, index: number): void {
  view.dispatch(view.state.tr.setMeta(mentionPickerKey, { select: index } satisfies PickerMeta));
}

export function dismissPicker(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(mentionPickerKey, { dismiss: true } satisfies PickerMeta));
}

/** Replaces `[[…` with a mention link to the chosen entry. */
export function insertMention(state: EditorState, open: OpenPicker, match: PickerMatch): Transaction {
  const link = entrySchema.marks['link']!.create({ href: `realmery:entry/${match.entry.id}` });
  const existing = state.doc.resolve(open.from).marks().filter((mark) => mark.type !== link.type);
  return state.tr
    .replaceWith(open.from, open.to, entrySchema.text(match.text, [...existing, link]))
    .removeStoredMark(link.type)
    .scrollIntoView();
}

export function chooseMatch(view: EditorView, index: number): void {
  const open = openPicker(view.state);
  const match = open?.matches[index];
  if (!open || !match) return;
  view.dispatch(insertMention(view.state, open, match));
  view.focus();
}

/** The toolbar's Mention button: starts a `[[` at the cursor, with any selected text as the query. */
export function startMention(view: EditorView): void {
  const { state } = view;
  const selected = state.doc.textBetween(state.selection.from, state.selection.to, ' ').slice(0, QUERY_MAX);
  view.dispatch(state.tr.insertText(`${TRIGGER}${selected}`, state.selection.from, state.selection.to).scrollIntoView());
  view.focus();
}
